import { colIndex, colName } from './types';

export type Node =
  | { t: 'num'; v: number }
  | { t: 'str'; v: string }
  | { t: 'bool'; v: boolean }
  | { t: 'err'; v: string }
  | { t: 'ref'; sheet?: string; r: number; c: number; absR: boolean; absC: boolean }
  | { t: 'range'; sheet?: string; sr: number; sc: number; er: number; ec: number }
  | { t: 'bin'; op: string; l: Node; r: Node }
  | { t: 'un'; op: string; x: Node }
  | { t: 'pct'; x: Node }
  | { t: 'call'; name: string; args: Node[] };

interface Tok { t: string; v: string }

const OPS = ['<>', '<=', '>=', '=', '<', '>', '+', '-', '*', '/', '^', '&', '%', '(', ')', ',', ':', '!', ';'];

export function tokenize(src: string): Tok[] {
  const toks: Tok[] = [];
  let i = 0;
  while (i < src.length) {
    const ch = src[i];
    if (ch === ' ' || ch === '\t' || ch === '\n') { i++; continue; }
    if (ch === '"') {
      let s = '';
      i++;
      while (i < src.length) {
        if (src[i] === '"') {
          if (src[i + 1] === '"') { s += '"'; i += 2; continue; }
          i++;
          break;
        }
        s += src[i++];
      }
      toks.push({ t: 'str', v: s });
      continue;
    }
    if (ch === "'") {
      let s = '';
      i++;
      while (i < src.length && src[i] !== "'") s += src[i++];
      i++;
      toks.push({ t: 'qname', v: s });
      continue;
    }
    if (/[0-9]/.test(ch) || (ch === '.' && /[0-9]/.test(src[i + 1] || ''))) {
      const m = /^[0-9]*\.?[0-9]+(?:[eE][+-]?[0-9]+)?/.exec(src.slice(i))!;
      toks.push({ t: 'num', v: m[0] });
      i += m[0].length;
      continue;
    }
    if (/[A-Za-z_$#]/.test(ch)) {
      const m = /^#[A-Z0-9/!?]+|^\$?[A-Za-z_][A-Za-z0-9_.$]*/.exec(src.slice(i))!;
      toks.push({ t: 'ident', v: m[0] });
      i += m[0].length;
      continue;
    }
    const two = src.slice(i, i + 2);
    if (OPS.includes(two)) { toks.push({ t: 'op', v: two }); i += 2; continue; }
    if (OPS.includes(ch)) { toks.push({ t: 'op', v: ch }); i++; continue; }
    throw new Error('Unexpected character: ' + ch);
  }
  return toks;
}

const CELL_RE = /^(\$?)([A-Za-z]{1,3})(\$?)(\d+)$/;
const COL_RE = /^(\$?)([A-Za-z]{1,3})$/;

function asCell(v: string): { r: number; c: number; absR: boolean; absC: boolean } | null {
  const m = CELL_RE.exec(v);
  if (!m) return null;
  const c = colIndex(m[2].toUpperCase());
  const r = Number(m[4]) - 1;
  if (r < 0 || r > 1048575 || c > 16383) return null;
  return { r, c, absR: m[3] === '$', absC: m[1] === '$' };
}

export function parseFormula(src: string): Node {
  const toks = tokenize(src);
  let p = 0;
  const peek = () => toks[p];
  const next = () => toks[p++];
  const eat = (v: string) => {
    const t = toks[p];
    if (!t || t.t !== 'op' || t.v !== v) throw new Error('Expected ' + v);
    p++;
  };

  function atom(): Node {
    const t = next();
    if (!t) throw new Error('Unexpected end of formula');
    if (t.t === 'num') return { t: 'num', v: Number(t.v) };
    if (t.t === 'str') return { t: 'str', v: t.v };
    if (t.t === 'op' && t.v === '(') {
      const e = expr(0);
      eat(')');
      return e;
    }
    if (t.t === 'op' && (t.v === '-' || t.v === '+')) return { t: 'un', op: t.v, x: signed() };
    if (t.t === 'qname') {
      eat('!');
      return refOrRange(t.v);
    }
    if (t.t === 'ident') {
      const v = t.v;
      if (v[0] === '#') return { t: 'err', v };
      const upper = v.toUpperCase();
      if (peek()?.t === 'op' && peek().v === '(') {
        next();
        const args: Node[] = [];
        if (!(peek()?.t === 'op' && peek().v === ')')) {
          for (;;) {
            args.push(expr(0));
            const n = peek();
            if (n?.t === 'op' && (n.v === ',' || n.v === ';')) { next(); continue; }
            break;
          }
        }
        eat(')');
        return { t: 'call', name: upper, args };
      }
      if (peek()?.t === 'op' && peek().v === '!') {
        next();
        return refOrRange(v);
      }
      if (upper === 'TRUE') return { t: 'bool', v: true };
      if (upper === 'FALSE') return { t: 'bool', v: false };
      return cellToken(v, undefined);
    }
    throw new Error('Unexpected token: ' + t.v);
  }

  function cellToken(v: string, sheet: string | undefined): Node {
    const cell = asCell(v);
    if (cell) {
      if (peek()?.t === 'op' && peek().v === ':') {
        const save = p;
        next();
        const t2 = peek();
        if (t2?.t === 'ident') {
          const cell2 = asCell(t2.v);
          if (cell2) {
            next();
            return {
              t: 'range', sheet,
              sr: Math.min(cell.r, cell2.r), sc: Math.min(cell.c, cell2.c),
              er: Math.max(cell.r, cell2.r), ec: Math.max(cell.c, cell2.c),
            };
          }
        }
        p = save;
      }
      return { t: 'ref', sheet, r: cell.r, c: cell.c, absR: cell.absR, absC: cell.absC };
    }
    const colM = COL_RE.exec(v);
    if (colM && peek()?.t === 'op' && peek().v === ':') {
      const save = p;
      next();
      const t2 = peek();
      const colM2 = t2?.t === 'ident' ? COL_RE.exec(t2.v) : null;
      if (colM2) {
        next();
        const c1 = colIndex(colM[2].toUpperCase());
        const c2 = colIndex(colM2[2].toUpperCase());
        return { t: 'range', sheet, sr: 0, sc: Math.min(c1, c2), er: -1, ec: Math.max(c1, c2) };
      }
      p = save;
    }
    throw new Error('Unknown name: ' + v);
  }

  function refOrRange(sheet: string): Node {
    const t = next();
    if (!t || t.t !== 'ident') throw new Error('Expected cell reference after sheet name');
    return cellToken(t.v, sheet);
  }

  function signed(): Node {
    let node = atom();
    while (peek()?.t === 'op' && peek().v === '%') { next(); node = { t: 'pct', x: node }; }
    return node;
  }

  function prefixed(): Node {
    let node = signed();
    while (peek()?.t === 'op' && peek().v === '^') {
      next();
      node = { t: 'bin', op: '^', l: node, r: signed() };
    }
    return node;
  }

  const PREC: Record<string, number> = { '=': 1, '<>': 1, '<': 1, '>': 1, '<=': 1, '>=': 1, '&': 2, '+': 3, '-': 3, '*': 4, '/': 4 };

  function expr(min: number): Node {
    let left = prefixed();
    for (;;) {
      const t = peek();
      if (!t || t.t !== 'op') break;
      const prec = PREC[t.v];
      if (prec == null || prec < min) break;
      next();
      const right = expr(prec + 1);
      left = { t: 'bin', op: t.v, l: left, r: right };
    }
    return left;
  }

  const node = expr(0);
  if (p < toks.length) throw new Error('Unexpected trailing input');
  return node;
}

export function walkRefs(node: Node, fn: (n: Node) => void) {
  switch (node.t) {
    case 'ref':
    case 'range':
      fn(node);
      return;
    case 'bin':
      walkRefs(node.l, fn);
      walkRefs(node.r, fn);
      return;
    case 'un':
    case 'pct':
      walkRefs(node.x, fn);
      return;
    case 'call':
      node.args.forEach((a) => walkRefs(a, fn));
      return;
  }
}

export function adjustFormula(src: string, dr: number, dc: number): string {
  let toks: Tok[];
  try {
    toks = tokenize(src);
  } catch {
    return src;
  }
  let out = '';
  for (let i = 0; i < toks.length; i++) {
    const t = toks[i];
    if (t.t === 'str') { out += '"' + t.v.replace(/"/g, '""') + '"'; continue; }
    if (t.t === 'qname') { out += "'" + t.v + "'"; continue; }
    if (t.t === 'ident') {
      const nextOp = toks[i + 1]?.t === 'op' ? toks[i + 1].v : '';
      const isFn = nextOp === '(';
      const isSheetName = nextOp === '!';
      const cell = isFn || isSheetName ? null : asCell(t.v);
      if (cell) {
        const r = cell.absR ? cell.r : cell.r + dr;
        const c = cell.absC ? cell.c : cell.c + dc;
        if (r < 0 || c < 0) { out += '#REF!'; continue; }
        out += (cell.absC ? '$' : '') + colName(c) + (cell.absR ? '$' : '') + (r + 1);
        continue;
      }
      out += t.v;
      continue;
    }
    out += t.v;
  }
  return out;
}
